import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useImperativeHandle,
  useState,
  useMemo,
  useCallback,
} from "react";
import { Input, Form, Alert, message } from "antd";
import { UserOutlined, SafetyOutlined } from "@ant-design/icons";
import { FormInstance } from "../../../../hooks/createForm";
import { SendPhoneCode } from "../../../SendPhoneCode/index";
import { EmailScene, User } from "authing-js-sdk";
import { useTranslation } from "react-i18next";
import { ScreenSize } from "@/constants/enum";
import { useScreenSize } from "@/hooks/useScreenSize";
import { getUserRegisterParams, isEmail, isPhone } from "../../../../utils";
import querystring from "querystring";
import { useNavigate } from "react-router";
import { ReadAndAccept } from "@/components/ReadAndAccept";
import { Agreement } from "@/config";
import { SendEmailCode } from "@/components/SendEmailCode";

export type VerifyLoginMethods = 'email-code' | 'phone-code'

export interface VerifyCodeLoginFormProps
  extends React.HTMLAttributes<HTMLDivElement> {
  onSubmit?: () => void;
  onSuccess?: (user: User) => void;
  onFail?: (error: any) => void;
  onFinishFailed?: (error: any) => void;
  agreements: Agreement[];
}

const InternalVerifyCodeLoginForm: ForwardRefRenderFunction<
  FormInstance,
  VerifyCodeLoginFormProps
> = ({ onSubmit, onSuccess, onFail, onFinishFailed, agreements }, ref) => {
  const [rawForm] = Form.useForm();
  const config = window.__config__;
  const [screenSize] = useScreenSize();
  const { t } = useTranslation();
  /** 表单是否被提交校验过 */
  const [validated, setValidated] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [identify, setIdentify] = useState('')


  const methods = useMemo<VerifyLoginMethods[]>(
    () => config?.verifyCodeTabConfig?.enabledLoginMethods ?? ['phone-code'],
    [config?.verifyCodeTabConfig]
  )

  const [currentMethod, setCurrentMethod] = useState(methods[0])
  //   // 控制强制修改密码弹窗
  //   const [initialPassword, setInitialPassword] = useState(false);
  //   // 临时token
  //   const [initToken, setInitToken] = useState("");
  //   // 超时时间
  //   const [expTime, setExpTime] = useState(0);

  const navigate = useNavigate();

  const onFinish = async (values: any) => {
    setValidated(true);
    if (config.agreementEnabled && agreements?.length && !accepted) {
      onFinishFailed?.("error")
      message.error(t('common.loginProtocolTips'))
      return;
    }
    onSubmit && onSubmit();
    try {
      const authenticationClient = window.__authing__;
      const { identify, code } = values;
      if (currentMethod === 'phone-code') {
        const user = await authenticationClient.loginByPhoneCode(identify, code, {
          customData: getUserRegisterParams(),
        } as any);
        onSuccess && onSuccess(user);
      } else {
        const user = await authenticationClient.loginByEmailCode(identify, code);
        onSuccess && onSuccess(user);
      }

    } catch (error: any) {
      onFail && onFail(error);

      if (error.code === 1639) {
        // 临时 token，需要携带此 token 进行密码重置
        const { token } = error.data;

        navigate({
          pathname: "/initial-password?",
          search: querystring.stringify(
            Object.assign(querystring.parse(window.location.search), {
              initial_token: token,
            })
          ),
        });
      }
    }
  };

  useImperativeHandle(ref, () => ({
    submit: async () => {
      try {
        await rawForm.validateFields();
        rawForm.submit();
      } catch (error: any) {
        onFail && onFail(error);
      }
    },
  }));

  const renderTemplate = useMemo(() => {
    if (methods.length !== 1) {
      return (
        <>
          {
            config.ssoPageComponentDisplay.autoRegisterThenLoginHintInfo && (
              <Alert
                message={t('login.allAutoRegister')}
                style={{ marginBottom: 24 }}
              />
            )
          }
          < Form.Item
            name="identify"
            rules={
              [
                {
                  required: true,
                  message: t("login.inputPhoneOrEmail"),
                  whitespace: true
                },
              ]}
          >
            <Input
              onChange={(e) => {
                let v = e.target.value
                setIdentify(v)
                if (isEmail(v)) {
                  setCurrentMethod('email-code')
                }
                if (isPhone(v)) {
                  setCurrentMethod('phone-code')
                }
              }}
              value={identify}
              size="large"
              placeholder={t("login.inputPhoneOrEmail")}
              prefix={<UserOutlined style={{ color: "#ddd" }} />}
            />
          </Form.Item >
        </>
      )
    }
    switch (methods[0]) {
      case 'phone-code':
        return (
          <>
            {
              config.ssoPageComponentDisplay.autoRegisterThenLoginHintInfo && (
                <Alert
                  message={t("login.phoneAutoRegister")}
                  style={{ marginBottom: 24 }}
                />
              )
            }
            <Form.Item
              name="identify"
              rules={[
                {
                  required: true,
                  message: t("login.inputPhone"),
                  whitespace: true
                },
              ]}
            >
              <Input
                autoComplete="tel"
                value={identify}
                onChange={(e) => {
                  setIdentify(e.target.value);
                }}
                size="large"
                placeholder={t("login.inputPhone")}
                prefix={<UserOutlined style={{ color: "#ddd" }} />}
              />
            </Form.Item>
          </>
        )
      case 'email-code':
        return (
          <>
            {
              config.ssoPageComponentDisplay.autoRegisterThenLoginHintInfo && (
                <Alert
                  message={t('login.emailAutoRegister')}
                  style={{ marginBottom: 24 }}
                />
              )
            }
            <Form.Item
              name="identify"
              rules={[
                {
                  required: true,
                  message: t("login.inputEmail"),
                  whitespace: true
                },
              ]}
            >
              <Input
                autoComplete="email"
                value={identify}
                onChange={(e) => {
                  setIdentify(e.target.value);
                }}
                size="large"
                placeholder={t("login.inputEmail")}
                prefix={<UserOutlined style={{ color: "#ddd" }} />}
              />
            </Form.Item>
          </>
        )
    }
  }, [methods])

  const SendCode = useCallback((props: any) => {
    if (currentMethod === 'email-code') {
      return (<SendEmailCode email={identify} scene={EmailScene.MfaVerify} />)
    } else {
      return (<SendPhoneCode phone={identify} />)
    }
  }, [identify, currentMethod])


  return (
    <>
      <Form
        form={rawForm}
        onFinishFailed={onFinishFailed}
        onSubmitCapture={onSubmit}
        onFinish={onFinish}
      >
        <button type="submit" hidden></button>

        {config.ssoPageComponentDisplay.phoneCodeInput && (
          <>

            {renderTemplate}
            <Form.Item
              name="code"
              validateFirst
              rules={[
                {
                  required: true,
                  message: t("login.inputFourVerifyCode", {
                    length: window.__config__.verifyCodeLength,
                  }),
                },
                {
                  validator: (_, value) => {
                    if (value.length > window.__config__.verifyCodeLength) {
                      return Promise.reject(
                        t("login.inputFourMsgVerifyCode", {
                          length: window.__config__.verifyCodeLength,
                        })
                      );
                    } else {
                      return Promise.resolve();
                    }
                  },
                },
              ]}
            >
              <Input
                className="authing-verify-code-input"
                autoComplete="one-time-code"
                size="large"
                maxLength={window.__config__.verifyCodeLength}
                placeholder={t("login.inputFourVerifyCode", {
                  length: window.__config__.verifyCodeLength,
                })}
                prefix={<SafetyOutlined style={{ color: "#ddd" }} />}
                addonAfter={
                  screenSize !== ScreenSize.Mobile && (
                    <SendCode />
                  )
                }
                suffix={
                  screenSize === ScreenSize.Mobile && (
                    <SendCode />
                  )
                }
              />
            </Form.Item>
            {config.agreementEnabled && Boolean(config.agreements?.length) && (
              <>
                <ReadAndAccept
                  onChange={setAccepted}
                  agreements={agreements}
                  showError={validated}
                />
                <div style={{ marginBottom: 20 }}></div>
              </>
            )}
          </>
        )}
      </Form>
    </>
  );
};

export const VerifyCodeLoginForm = forwardRef<
  FormInstance,
  VerifyCodeLoginFormProps
>(InternalVerifyCodeLoginForm);
